import { GameState, LobbyState, Role } from "../types";
import { getNextPresident, governmentFailed, isTermLimited } from "./nextPresident";
import { rulesFor } from "./rules";

/** One short tip. */
export type Tip = {
  text: string;
  /** "now": something to do; "warn": something dangerous; the rest is information. */
  tone?: "now" | "warn";
};

export type TipSection = {
  title: string;
  tips: Tip[];
};

const list = (names: string[]) => (names.length <= 1 ? names.join("") : names.slice(0, -1).join(", ") + " and " + names[names.length - 1]);
const plural = (n: number, one: string, many: string) => n + " " + (n === 1 ? one : many);

/** What the player can do or should know about the current step of the turn. */
function rightNow(game: GameState, user: string): Tip[] {
  const me = game.players[user];
  if (me && !me.alive) {
    return [{ text: "You have been executed: you may watch, but you can no longer speak, vote or hold office." }];
  }
  const isPresident = game.president === user;
  const isChancellor = game.chancellor === user && !governmentFailed(game);
  const zone = game.fascistPolicies >= 3;
  const voted = game.userVotes && game.userVotes[user] !== undefined;

  switch (game.state) {
    case LobbyState.CHANCELLOR_NOMINATION:
      if (isPresident) {
        const tips: Tip[] = [{ tone: "now", text: "You are the President: nominate a Chancellor. The others then vote on your pair." }];
        const limited = game.playerOrder.filter((p) => game.players[p].alive && isTermLimited(game, p));
        if (limited.length > 0) {
          tips.push({ text: list(limited) + " cannot be nominated now (term limit)." });
        }
        if (zone) {
          tips.push({ tone: "warn", text: "Hitler zone: if your nominee is Hitler and the vote passes, the Fascists win. Pick someone you have a reason to trust." });
        }
        return tips;
      }
      return [
        { text: game.president + " is choosing a Chancellor." },
        ...(zone ? [{ tone: "warn" as const, text: "Hitler zone: if the nominee is Hitler and the vote passes, the Fascists win. Vote Nein if you doubt them." }] : []),
      ];

    case LobbyState.CHANCELLOR_VOTING:
      if (voted) {
        return [{ text: "Your vote is in. Waiting for the others." }];
      }
      return [
        { tone: "now", text: "Vote Ja to approve " + game.president + " and " + game.chancellor + " as the government, or Nein to reject them. More Ja than Nein is needed; a tie fails." },
        { text: "A rejected government moves the election tracker up by one." },
        ...(zone ? [{ tone: "warn" as const, text: "Hitler zone: if " + game.chancellor + " is Hitler and this passes, the Fascists win." }] : []),
      ];

    case LobbyState.LEGISLATIVE_PRESIDENT:
      return isPresident
        ? [
            { tone: "now", text: "You drew 3 policies: discard one and pass the other two to the Chancellor." },
            { text: "Nobody can check what you drew, so you may say anything about it. Your word is all the others have." },
          ]
        : [{ text: game.president + " is looking at 3 policies and will pass 2 to " + game.chancellor + "." }];

    case LobbyState.LEGISLATIVE_CHANCELLOR:
      return isChancellor
        ? [
            { tone: "now", text: "Enact one of the two policies the President passed you; the other is discarded." },
            ...(game.fascistPolicies >= 5 ? [{ text: "With 5 Fascist policies in place you may also propose a veto. The President must agree." }] : []),
          ]
        : [{ text: game.chancellor + " chooses which of the President's two policies is enacted. Only these two know what was on offer." }];

    case LobbyState.LEGISLATIVE_PRESIDENT_VETO:
      return isPresident
        ? [{ tone: "now", text: "The Chancellor asks for a veto. Agree and both policies are discarded (the election tracker goes up), or refuse and they must enact one." }]
        : [{ text: game.chancellor + " asked for a veto; " + game.president + " decides." }];

    case LobbyState.PP_PEEK:
      return isPresident
        ? [{ tone: "now", text: "Peek: you see the top three policies of the deck. You may tell the others whatever you like." }]
        : [{ text: game.president + " is looking at the top three policies." }];

    case LobbyState.PP_INVESTIGATE:
      return isPresident
        ? [
            { tone: "now", text: "Investigate: pick a player and learn their party, Liberal or Fascist. Hitler counts as Fascist." },
            { text: "What you tell the others is up to you: nobody can check it." },
          ]
        : [{ text: game.president + " is choosing a player to investigate. They learn that player's party, not the exact role." }];

    case LobbyState.PP_ELECTION:
      return isPresident
        ? [{ tone: "now", text: "Special election: choose any other living player as the next President. The usual order then continues from your seat." }]
        : [{ text: game.president + " is choosing the next President." }];

    case LobbyState.PP_EXECUTION:
      return isPresident
        ? [
            { tone: "now", text: "Execution: pick a player to execute. If they are Hitler, the Liberals win at once." },
            { text: "The table only learns whether the player was Hitler, never their party." },
          ]
        : [{ text: game.president + " is choosing a player to execute. If it is Hitler, the Liberals win." }];

    case LobbyState.POST_LEGISLATIVE: {
      const next = getNextPresident(game);
      return isPresident
        ? [{ tone: "now", text: "Press END TERM when you are ready" + (next ? ": the presidency passes to " + next + "." : ".") }]
        : [{ text: "Waiting for " + game.president + " to end their term." }];
    }

    default:
      return [];
  }
}

/** Facts about the board for this table size. */
function theBoard(game: GameState): Tip[] {
  const count = game.playerOrder.length;
  const rules = rulesFor(count);
  const tips: Tip[] = [];
  const liberal = game.liberalPolicies ?? 0;
  const fascist = game.fascistPolicies ?? 0;

  tips.push({
    text:
      "Liberals win with " + plural(5 - liberal, "more Liberal policy", "more Liberal policies") + ", or by executing Hitler.",
  });
  tips.push({
    text:
      "Fascists win with " + plural(6 - fascist, "more Fascist policy", "more Fascist policies") +
      ", or by electing Hitler Chancellor once 3 Fascist policies are in.",
  });

  if (fascist < 6) {
    const slot = rules.track[fascist];
    let text = "The next Fascist policy is number " + (fascist + 1) + ": ";
    if (slot.none) {
      text += "no power for the President.";
    } else if (fascist === 5) {
      text += "the Fascists win.";
    } else {
      text += slot.title + ". " + slot.text;
    }
    if (fascist === 2) {
      text += " After it the Hitler zone begins.";
    }
    tips.push({ tone: slot.none || fascist === 5 ? undefined : "warn", text });
  }
  tips.push({
    text:
      liberal === 4
        ? "The next Liberal policy wins the game for the Liberals."
        : "A Liberal policy gives the President no power.",
  });

  if (fascist >= 3) {
    tips.push({
      tone: "warn",
      text: "The Hitler zone is on: a passed vote on Hitler as Chancellor ends the game. After every passed vote the table learns whether the Chancellor is Hitler.",
    });
  }

  const tracker = game.electionTracker ?? 0;
  tips.push({
    tone: tracker === 2 ? "warn" : undefined,
    text:
      "Election tracker: " + tracker + " of 3. " +
      (tracker === 2
        ? "One more rejected government and the top policy of the deck is enacted at random."
        : "At 3, the top policy of the deck is enacted at random."),
  });

  const next = getNextPresident(game);
  if (next) {
    tips.push({ text: "President after this term: " + next + "." });
  }
  if (game.drawSize !== undefined) {
    tips.push({ text: "Draw pile: " + game.drawSize + " cards. The deck started with 6 Liberal and 11 Fascist policies." });
  }
  return tips;
}

/** Light advice for a beginner, depending on the player's own role and the situation. */
function goodToKnow(game: GameState, user: string): Tip[] {
  const me = game.players[user];
  const role = me?.id;
  const count = game.playerOrder.length;
  const rules = rulesFor(count);
  const tips: Tip[] = [];

  if (role === Role.LIBERAL) {
    tips.push({ text: "You are a Liberal: you know nobody's role. The voting record (VOTES) is the best evidence you have: who keeps approving governments that enact Fascist policies?" });
    tips.push({ text: "Go easy on accusations: 11 of the 17 policies are Fascist, so even an honest government is sometimes forced to enact one." });
    if (game.fascistPolicies >= 3) {
      tips.push({ text: "In the Hitler zone, vote Nein on a Chancellor you are unsure about. Any player could be Hitler, and a Nein costs less than a lost game." });
    }
  } else if (role === Role.FASCIST) {
    tips.push({ text: "You are a Fascist: you know your team" + (rules.hitlerKnowsFascists ? ", and Hitler knows you" : ", but Hitler does not know you") + ". Hitler winning the election is your main goal." });
    tips.push({ text: "Do not look coordinated. Voting differently from your team now and then, or passing a Liberal policy early, builds trust for later." });
    if (game.fascistPolicies >= 3) {
      tips.push({ text: "The Hitler zone is on: get Hitler elected Chancellor." });
    }
  } else if (role === Role.HITLER) {
    tips.push({
      text: "You are Hitler: " + (rules.hitlerKnowsFascists ? "you know your Fascist teammate." : "you do not know who the Fascists are, but they know you.") + " Play like a Liberal and stay out of the spotlight.",
    });
    tips.push({ text: "After 3 Fascist policies, a passed vote with you as Chancellor wins the game. Before that, being accused of being Hitler is dangerous: an execution would end it." });
  }

  tips.push({ text: "Anyone may lie about anything except what they see on the board. Only the votes and the enacted policies are certain." });
  return tips;
}

/**
 * The tips for the current moment, in sections. They only use what the player may know: the rules, the board
 * and their own role.
 */
export function buildTips(game: GameState, user: string): TipSection[] {
  const sections: TipSection[] = [];
  const now = rightNow(game, user);
  if (now.length > 0) {
    sections.push({ title: "Right now", tips: now });
  }
  sections.push({ title: "The board", tips: theBoard(game) });
  sections.push({ title: "Good to know", tips: goodToKnow(game, user) });
  return sections;
}
